import React, { useState, useRef, useEffect } from 'react';
import { Play } from 'lucide-react';
import ReplayBadge from '../components/ReplayBadge';
import VideoSection from '../components/VideoSection';
import SpeakerList from '../components/SpeakerList';
import KeyTopics from '../components/KeyTopics';
import StickyWatchBar from '../components/StickyWatchBar';
import { pageData } from '../data/mockData';

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_page-recreate-1/artifacts/7bfrg8n8_now-next-talent-recording.mp4";

const TalkPage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const videoRef = useRef(null);
  const watchBtnRef = useRef(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const scrollToVideo = () => {
    if (videoRef.current) {
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
          setIsPlaying(true);
        }
      }, 600);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    if (watchBtnRef.current) {
      observer.observe(watchBtnRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Gray background area behind video */}
      <div className="bg-gray-50">
        <div className="max-w-[620px] mx-auto px-5 pt-10">
          {/* Video inside main column */}
          <div className="relative rounded-2xl overflow-hidden shadow-lg bg-black" style={{ marginBottom: '-10px', position: 'relative', zIndex: 10 }}>
            <video
              ref={videoRef}
              src={VIDEO_URL}
              poster="https://customer-assets.emergentagent.com/job_page-recreate-1/artifacts/hveu8w87_ORG-Email%20Cover%20v1.png"
              controls={isPlaying}
              className="w-full aspect-video object-cover"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              preload="metadata"
            />
            {!isPlaying && (
              <div
                className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/20 transition-opacity hover:bg-black/10"
                onClick={handlePlay}
              >
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl transition-transform hover:scale-110">
                  <Play className="w-7 h-7 text-gray-900 ml-1 fill-gray-900" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[620px] mx-auto px-5 pt-6 pb-10 md:pb-14">
        {/* Replay Badge */}
        <div className="mb-6">
          <ReplayBadge text={pageData.badge} />
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-[32px] md:leading-[1.25] font-bold text-gray-900 mb-8 tracking-tight">
          {pageData.title}
        </h1>

        {/* Video Section - ref for intersection observer */}
        <div ref={watchBtnRef}>
          <VideoSection video={pageData.video} onWatch={scrollToVideo} />
        </div>

        {/* Body Paragraphs */}
        <div className="mt-8 flex flex-col gap-7">
          {pageData.paragraphs.map((para, index) => (
            <p
              key={index}
              className="text-[17px] leading-[1.85] text-black"
              dangerouslySetInnerHTML={{ __html: para.text }}
            />
          ))}
        </div>

        {/* Speakers */}
        <SpeakerList speakers={pageData.speakers} />

        {/* After Speakers Paragraph */}
        <div className="mt-7 mb-7">
          <p
            className="text-[17px] leading-[1.85] text-black"
            dangerouslySetInnerHTML={{ __html: pageData.afterSpeakers }}
          />
        </div>

        {/* Key Topics */}
        <KeyTopics topics={pageData.keyTopics} />

        {/* Hosted by Skillvue - inside main content */}
        <div className="mt-10 pt-8 border-t border-dashed border-gray-300">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_page-recreate-1/artifacts/ixaqaiy6_66cdde23b308922774c4b3e0_logomark%20%281%29.svg" alt="Skillvue" className="w-10 h-10" />
            <span className="text-[15px] text-gray-500 font-medium">Hosted by Skillvue</span>
          </div>
        </div>

        {/* Spacer for sticky bar */}
        <div className="h-24" />
      </main>

      {/* Sticky Watch Bar */}
      <StickyWatchBar visible={showStickyBar} onWatch={scrollToVideo} />
    </div>
  );
};

export default TalkPage;
